import { AppSidebar } from "@/components/layout/app-sidebar.tsx";
import { Separator } from "@/components/shadcn/separator.tsx";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/shadcn/sidebar.tsx";
import Loading from "@/components/Loading";
import { Link, Outlet } from "react-router-dom";
import React, {Suspense, useContext, useEffect, useState} from "react";
import { ToastContainer } from "react-toastify";
import { NavUser } from "@/components/layout/nav-user.tsx";
import Breadcrumbs from "@/components/layout/breadcrumbs.tsx";
import { ChatbotProvider } from "@/store/context/ChatbotContext.tsx";
import {Icon} from "@/components/Icon.tsx";
import { UserContext } from "@/store/context/UserContext.tsx";
import {isPlatform, platformMostAI, platformWizyChat} from "@/domains.ts";
import {PwaModal} from "@/components/layout/PwaModal.tsx";
import {useTranslation} from "react-i18next";
import {cn} from "@/utils/shadcn.ts";
import {LoginModal} from "@/components/layout/LoginModal.tsx";
import SwitchDark from "@/components/partials/header/Tools/SwitchDark";

interface LayoutProps {
  fullWidth?: any;
}

export default function ShadLayout({ fullWidth }: LayoutProps) {
  const { userSettings } = useContext(UserContext);
  const {t} = useTranslation();

  const [sidebarOpened, setSidebarOpened] = useState(true);

  // TODO SHADCN hope this temporary solution : https://github.com/shadcn-ui/ui/issues/4900
  useEffect(() => {
    // Callback to handle DOM changes
    const handleDomChanges = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          const pointerEvents = document.body.style.pointerEvents;
          console.log("Pointer Events changed:", pointerEvents);

          // Reset pointer-events if set to 'none'
          if (pointerEvents === "none") {
            document.body.style.pointerEvents = "";
            console.log("Pointer Events reset to default");
          }
        }
      }
    };

    // Create a MutationObserver instance
    const observer = new MutationObserver(handleDomChanges);

    // Start observing the body element for style attribute changes
    observer.observe(document.body, {
      attributes: true, // Watch for attribute changes
      attributeFilter: ["style"], // Only observe changes to 'style'
    });

    // Cleanup the observer when the component unmounts
    return () => observer.disconnect();
  }, []); // Empty dependency array ensures it runs once

  return (
    <SidebarProvider open={sidebarOpened} onOpenChange={setSidebarOpened}>
      {/*{isPlatform(platformMostAI) && <PwaModal />}*/}
      <LoginModal/>
      <ToastContainer />
      <AppSidebar />
      <SidebarInset>
        <header className="flex flex-row p-1 md:p-2 h-12 md:h-16 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <Breadcrumbs />
            </div>
            <div className="flex-grow"></div>
            <div className="flex items-center">

              <SwitchDark />

              {isPlatform(platformWizyChat) && (
                <a
                  className="hidden lg:flex items-center cursor-pointer hover:underline"
                  href="https://wizychat.canny.io/changelog"
                  target="_blank"
                >
                  <Icon
                    icon="fluent:megaphone-loud-20-regular"
                    height="20"
                    className="mr-2"
                  />
                  Changelog
                </a>
              )}

              <Separator orientation="vertical" className="mx-2 h-4" />

              {userSettings.userID && <NavUser />}
              {!userSettings.userID && (
                <Link to="/login">
                  <div className="flex items-center py-1 px-2">
                    <Icon
                      icon="codicon:sign-in"
                      height={32}
                      width={32}
                      className="w-8 inline-block mr-1"
                    />
                    <span>{t("login_page.sign_in")}</span>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </header>
        <div
          className={cn(
            "h-full bg-slate-200 dark:bg-slate-900 p-2 lg:p-4 overflow-y-auto overflow-x-auto ",
            {
              // "w-[calc(100vw)]" : isMobile,
              // "w-[calc(100vw-255px)]" : !isMobile && sidebarOpened,
              // "w-[calc(100vw-48px)]" : !isMobile && !sidebarOpened,
            },
          )}
        >
          <ChatbotProvider>
            {fullWidth && (
              <Suspense fallback={<Loading />}>{<Outlet />}</Suspense>
            )}
            {!fullWidth && (
              <div className="md:container !px-0">
                <Suspense fallback={<Loading />}>{<Outlet />}</Suspense>
              </div>
            )}
          </ChatbotProvider>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
